import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Button, CheckboxCheckedIcon, CloseIcon, ErrorCircleIcon, Text, View } from '@upstox/upstox-ui';
import { Timer } from 'utils/helpers';
import { TOAST_ANIMATION_DELAY } from './constants';
import '../../style.css';
import * as css from './styles';

type Props = {
  id: string;
  toast: ToastOptions;
  deleteToast: (id: string) => void;
  className?: string;
};

const VisibleToast: React.FC<Props> = (
  { id, toast, deleteToast, className },
) => {
  const [isVisible, setIsVisible] = useState(false);

  const timerRef = useRef(
    new Timer(() => {
      setIsVisible(false);
      setTimeout(() => deleteToast(id), TOAST_ANIMATION_DELAY);
    }, toast.timeout),
  );

  useEffect(() => {
    const timer = timerRef.current;
    setIsVisible(true);
    timer.start();

    return () => {
      timer.stop();
    };
  }, [deleteToast, id]);

  const handleMouseEnter = useCallback(() => timerRef.current.pause(), []);
  const handleMouseLeave = useCallback(() => timerRef.current.resume(), []);
  const handleDelete = useCallback(() => deleteToast(id), [deleteToast, id]);

  const { title, icon: Icon, iconProps, text, type, action } = toast;

  const renderIcon = () => {
    if (typeof Icon === 'function') {
      return <Icon {...iconProps} />;
    }
    if (Icon) {
      return Icon;
    }
    return type === 'error'
      ? <ErrorCircleIcon color="text.negative" />
      : <CheckboxCheckedIcon color="text.positive" />;
  };

  return (
    <CSSTransition
      in={isVisible}
      timeout={TOAST_ANIMATION_DELAY}
      classNames={`${className} item`}
      unmountOnExit
    >
      <View
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        flexDirection="column"
        css={css.toast}
        cssProps={{ type }}
      >
        {/* Notification Header */}
        <View css={css.header} alignItems="center">
          {renderIcon()}
          <Text css={css.titleText}>{title}</Text>
          <CloseIcon css={css.closeIcon} onClick={handleDelete} />
        </View>

        {/* Notification Body */}
        {text && <Text css={css.body}>{text}</Text>}
        {/* Notification Footer */}
        <View css={css.footer}>
          {action
            ? (
              <Button variant="white" css={css.actionButton} onClick={action?.actionCallback}>
                <Text weight="bold" color="interactive.primary.default">
                  {action?.btnText}
                </Text>
              </Button>
            )
          : null}
        </View>
      </View>
    </CSSTransition>
  );
};

export default VisibleToast;
